import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { MyInfo, PortalUserResponse } from '@/types/api';
import { INIT_MY_INFO } from '@/recoil/init_data';
import { createSlice } from '@reduxjs/toolkit';
import { getCookie } from '@/utils/tokens';
import { COOKIE_KEY_ACCESS_TOKEN } from '@/constant/keys';
import { fetchApi } from '@/utils/api/customAxios';
import { MYINFO_URL, PORTAL_PASSPORT_URL } from '@/constant/apiList';

type AuthContextType = AuthStateType & {
  method: string;
};

export type AuthStateType = {
  isInitialized: boolean;
  isLoading: boolean;
  error: Error | string | null;
  myInfo: MyInfo;
  isGuest: boolean;
};

const initialState: AuthStateType = {
  isInitialized: false,
  isLoading: false,
  error: null,
  myInfo: JSON.parse(JSON.stringify(INIT_MY_INFO)),
  isGuest: true,
};

const useToolKitReducer = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    initialSuccess(state, action) {
      state.isInitialized = true;
      state.isLoading = false;
      state.myInfo = action.payload.myInfo;
      state.isGuest = action.payload.isGuest;
    },
  },
});

const AuthContext = createContext<AuthContextType | null>(null);
AuthContext.displayName = 'Auth';

const AuthProvider = ({ children }: PropsWithChildren) => {
  const { getInitialState, reducer, actions } = useToolKitReducer;
  const [state, dispatch] = useReducer(reducer, getInitialState());

  // 첫 페이지 랜더링시
  const _initialize = useCallback(async () => {
    dispatch(actions.startLoading());

    const accessToken = getCookie(COOKIE_KEY_ACCESS_TOKEN);

    try {
      if (accessToken) {
        const portalUserData = await fetchApi<{ data: PortalUserResponse }>({
          method: 'GET',
          url: PORTAL_PASSPORT_URL,
          extraHeaders: {
            Authorization: `Bearer ${getCookie(COOKIE_KEY_ACCESS_TOKEN)}`,
          },
        });

        const myInfo = await fetchApi<{ data: MyInfo }>({
          method: 'GET',
          url: MYINFO_URL,
        });

        dispatch(
          actions.initialSuccess({
            myInfo: {
              ...portalUserData.data.defaultPassport.detail,
              passportId: portalUserData.data.defaultPassport.passportId,
              userId: myInfo.data.userId,
              enterDate: myInfo.data.enterDate,
              employmentType: myInfo.data.employmentType,
              employmentTypeName: myInfo.data.employmentTypeName,
              employmentTypeDate: myInfo.data.employmentTypeDate,
              language: portalUserData.data.personalization.lang,
            },
            isGuest: false,
          }),
        );
      } else {
        dispatch(
          actions.initialSuccess({
            myInfo: initialState.myInfo,
            isGuest: true,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(
        actions.initialSuccess({
          myInfo: initialState.myInfo,
          isGuest: true,
        }),
      );
    }
  }, [actions]);

  useEffect(() => {
    if (_initialize) {
      _initialize();
    }
  }, [_initialize]);

  const memoizedValue = useMemo(
    () => ({
      method: 'auth',
      ...state,
    }),
    [state.isInitialized, state.isLoading, state.error],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
